import { h, JSX } from 'preact'
import { useState } from 'preact/hooks'
import { Message } from '../../../../lib/intl/Message'
import { Geografi, isGeografi, isParti, Parti, Valgtype } from '../../../domain'

import { OppslutningTable } from './OppslutningTable/OppslutningTable'
import { OppslutningTableItem } from './mapping'
import { useWidgetUrls } from '../../../routes/widget'
import { withSectionErrorBoundary } from '../../shared/SectionErrorBoundary/SectionErrorBoundary'
import { TabHeading } from '../../shared/Tabs/TabHeading'
import { TabPanel, Tabs } from '../../shared/Tabs/Tabs'
import { EndringsTable } from './EndringTable/EndringTable'

/* styles */
import styles from './Oppslutning.css'
import { useCustomContext } from '../../../../lib/context'
import { GA_EVENTS } from '../../../../lib/analytics'
import { WidgetFooter } from '../../shared/WidgetFooter/WidgetFooter'
import { JSXElement } from '@babel/types'
import { idFrom } from '../../../api'

type TabsId = 'antall' | 'endring'
type Props = {
  grunndata: Geografi | Parti
  items: OppslutningTableItem[]
  valgtype: 'kommune' | 'fylke'
  initiallyOpen?: TabsId
  maxItems?: number
  widgetFooter?: JSX.Element
}

const SHOW_MIN = 10

function _Oppslutning(props: Props) {
  const { grunndata, items, valgtype, initiallyOpen, maxItems = 500, widgetFooter } = props

  /* Viser ikke komponenten dersom vi ikke har rader */
  if (!items.length) {
    return null
  }

  // Vi kan ikke vise endring hvis endringene ikke finnes i alle items
  const hasEndring = items.some(item => item.endring !== undefined)
  const initiallyOpenTab = (hasEndring && initiallyOpen) || 'antall'

  const [activeTab, setActiveTab] = useState<TabsId>(initiallyOpenTab)
  const { oppslutningUrl } = useWidgetUrls()

  const widgetUrl = oppslutningUrl(grunndata, valgtype, activeTab)
  const type = isParti(grunndata) ? 'steder' : 'partier'
  const navn = grunndata.navn

  function onChange(idx) {
    setActiveTab(idx === 1 ? 'endring' : 'antall')
  }

  return (
    <section
      class={styles.oppslutning}
      data-widget-url={widgetUrl}
      data-valgtype={valgtype}
      data-grunndata={isGeografi(grunndata) ? idFrom(grunndata) : grunndata.id}
      data-modulnavn="oppslutning"
    >
      <h3>
        <Message
          id="oppslutning-tittel"
          values={{ navn, kortnavn: isParti(grunndata) ? grunndata.kortNavn : '' }}
          modifiers={{ parti: isParti(grunndata), fylkesvalg: valgtype === 'fylke' }}
        />
      </h3>
      <Tabs
        onChange={onChange}
        headings={[
          <TabHeading icon="tab-stemmer">
            <Message id={`oppslutning-tab-tittel-oppslutning`} />
          </TabHeading>,
          <TabHeading icon="tab-endring" disabled={!hasEndring}>
            <Message id={`oppslutning-tab-tittel-endring`} />
          </TabHeading>
        ]}
      >
        <TabPanel hidden={initiallyOpenTab === 'endring'}>
          <OppslutningTable
            items={items}
            type={type}
            minimumShowing={SHOW_MIN}
            maxItems={maxItems}
          />
        </TabPanel>
        <TabPanel hidden={initiallyOpenTab !== 'endring'}>
          <EndringsTable items={items} type={type} minimumShowing={SHOW_MIN} maxItems={maxItems} />
        </TabPanel>
      </Tabs>
      {widgetFooter}
    </section>
  )
}

export const Oppslutning = withSectionErrorBoundary(_Oppslutning)
